/**
 * Store backend related data for the TrendPivotChart.js component
 */
import immutableStore from 'utils/immutableStore';
import {List, Map, Record} from 'immutable';
import alt from 'components/Dispatcher';
import moment from 'moment';

import CallActions from 'actions/CallActions';
import TrendPivotChartActions from 'actions/calls/trend/TrendPivotChartActions';
import SearchStore from 'stores/SearchStore';
import TrendPivotChartSource from 'sources/calls/trend/TrendPivotChartSource';

// 7 days
const TREND_PIVOT_SEARCH_INTERNAL_IN_HOURS = 24 * 7;

const TrendData = Record({
  buckets: List(),
  total: 0,
});
const PivotData = Record({
  pivot: null,
  buckets: List(),
  total: 0,
});
const SEARCH_METHODS = {
  trend: 'trend',
  pivot: 'pivot',
};

/** Store the search-fields part of SearchStore.state and the related trend / pivot field for comparison */
const TrendPivotState = Record({
  searchMaxDate: moment(),
  loading: false,
  trend: TrendData(),
  pivot: PivotData(),
  shouldRenderTrendButton: false,
  showTrendChart: false,
  fields: Map(),
});

@immutableStore
class TrendPivotChartStore {
  constructor() {
    this.state = new TrendPivotState();
    this.bindActions(CallActions);
    this.bindActions(TrendPivotChartActions);
    this.registerAsync(TrendPivotChartSource);
  }

  onSearchSuccess(data) {
    this.waitFor(SearchStore);
    let searchState = SearchStore.getState();
    const {searchFields} = searchState;
    const showTrendButton = !searchFields.isEmpty();
    this.setState({fields: searchFields});
    if (this.state.showTrendChart && showTrendButton) {
      this._searchTrendAndPivot();
    }
  }

  /**
   * Toggles the variable tracking if the Trend Chart should be shown.
   **/
  onToggleShowTrendChart() {
    this.setState({showTrendChart: !this.state.showTrendChart});
  }

  /**
   * Reset pivot state to the default.
   */
  clearPivotState() {
    this.setState({pivot: PivotData()});
  }

  onSearchTrend() {
    const searchFields = this.state.fields;
    this._search(searchFields, SEARCH_METHODS.trend);
  }

  onSearchTrendLoading() {
    this._setLoading(true);
  }

  onSearchTrendSuccess(resp) {
    const data = resp.data;
    const trendData = TrendData({
      buckets: this._bucketsToDataPoints(data['buckets']),
      total: data['total'],
    });
    this.setState({
      trend: trendData,
      loading: false,
    });
  }

  /** Remove `pivot` from this.fields and perform a /search request for a pivot results without the pivot field */
  onSearchPivot(pivot) {
    const searchFields = this.state.fields.filterNot((_, key) => key === pivot);
    this.setState({
      pivot: PivotData({pivot: pivot}),
    });
    this._search(searchFields, SEARCH_METHODS.pivot);
  }

  onSearchPivotLoading() {
    this._setLoading(true);
  }

  onSearchPivotSuccess(resp) {
    const data = resp.data;
    let pivot = this.state.pivot;
    pivot = PivotData({
      pivot: pivot.pivot,
      buckets: this._bucketsToDataPoints(data['buckets']),
      total: data['total'],
    });
    this.setState({pivot, loading: false});
  }

  onSwitchSearchMaxDate(direction) {
    if (direction === 'backward') {
      this.setState({
        searchMaxDate: this.state.searchMaxDate.subtract(12, 'month'),
      });
    } else if (direction === 'forward') {
      this.setState({
        searchMaxDate: this.state.searchMaxDate.add(12, 'month'),
      });
    }
    this._searchTrendAndPivot();
  }

  _searchTrendAndPivot() {
    if (!this.state.loading) {
      this.onSearchTrend();
      if (this.state.pivot.pivot) {
        this.onSearchPivot(this.state.pivot.pivot);
      }
    }
  }

  _search(searchFields, method) {
    const now = this.state.searchMaxDate;
    const oneYearAgo = moment(now).subtract(12, 'month');
    setTimeout(() => {
      if (method === SEARCH_METHODS.trend) {
        this.getInstance().searchTrend(
          searchFields,
          `${TREND_PIVOT_SEARCH_INTERNAL_IN_HOURS}h`,
          oneYearAgo,
          now
        );
      } else if (method === SEARCH_METHODS.pivot) {
        this.getInstance().searchPivot(
          searchFields,
          `${TREND_PIVOT_SEARCH_INTERNAL_IN_HOURS}h`,
          oneYearAgo,
          now
        );
      }
    }, 0);
  }

  _bucketsToDataPoints(buckets) {
    return List(buckets).map(bucket => this._bucketToDataPoint(bucket));
  }

  _bucketToDataPoint(bucket) {
    const date = moment(bucket.key).toDate();
    const value = bucket.doc_count;
    return {date: date, value: value};
  }

  _setLoading(loading) {
    this.setState({loading});
  }
}

export default alt.createStore(TrendPivotChartStore, 'TrendPivotChartStore');
